import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { IResolveParams } from "reactjs-social-login";

import { useAppDispatch } from "../../app/hook";
import {
  useSocialAuthCheckUserMutation,
  useSocialAuthLoginMutation
} from "../../app/services/auth-rtk.service";
import { AlertsType } from "../../constants/alerts.constant";
import { MultiLanguageUrl } from "../../constants/multiLanguage.constant";
import { setError } from "../../features/auth/auth.slice";

export type SocialAuthParamsType = {
  isSocialAuthLoading: boolean;
  handleLoginStartSocialAuth: () => void;
  handleResolveSocialAuth: (
    provider: IResolveParams["provider"],
    data: IResolveParams["data"]
  ) => void;
  handleRejectSocialAuth: (err: any) => void;
};

export function BusinessWithSocialAuth<
  P extends SocialAuthParamsType = SocialAuthParamsType
>(WrappedComponent: React.ComponentType<P>) {
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || "Component";

  const ComponentWithSocialAuth = (props: P) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();

    useEffect(() => {
      dispatch(setError(null));
    }, []);

    const [socialAuthLogin, { isLoading: isSocialAuthLoading }] =
      useSocialAuthLoginMutation();

    const [socialAuthCheckUser, setSocialAuthCheckUserStatus] =
      useSocialAuthCheckUserMutation();

    const handleLoginStartSocialAuth = () => {};

    const handleResolveSocialAuth = (
      provider: IResolveParams["provider"],
      data: IResolveParams["data"]
    ) => {
      dispatch(setError(null));

      const cpPayload = { ...data, provider };

      const requestData: any = { type: "login", providerPayload: cpPayload };

      socialAuthCheckUser({
        type: "business-partner",
        providerPayload: requestData
      })
        .unwrap()
        .then((res) => {
          sessionStorage.setItem(
            "business-partner",
            JSON.stringify({
              socialAuth: requestData
            })
          );
          navigate(
            `/${
              MultiLanguageUrl[i18n.language] || "en-US"
            }/partner/business/information`,
            {
              replace: true
            }
          );
        })
        .catch((err) => {
          dispatch(setError(err.data.message));
        });
    };

    const handleRejectSocialAuth = (err: any) => {
      if (err.error && err.error !== "popup_closed_by_user")
        toast.error(t(AlertsType.ISATKO017));
    };

    const withSocialAuthProps = {
      isSocialAuthLoading: isSocialAuthLoading,
      onLoginStartSocialAuthStart: handleLoginStartSocialAuth,
      onResolveSocialAuth: handleResolveSocialAuth,
      onRejectSocialAuth: handleRejectSocialAuth
    };

    return <WrappedComponent {...withSocialAuthProps} {...(props as P)} />;
  };

  ComponentWithSocialAuth.displayName = `withSocialAuth(${displayName}`;

  return ComponentWithSocialAuth;
}
